import { useCallback, useEffect, useReducer } from "react";
import { useParams } from "react-router-dom";

import { useGlobalContext } from "../context/ConversationsProvider";
import { useClientId } from "../context/ClientsProvider";

import { chatReducer, initialChatState } from "./chat/chatReducer";
import { useActiveConversation } from "./chat/useActiveConversation";
import { useNewConversation } from "./chat/useNewConversation";
import { useSendMessage } from "./chat/useSendMessage";
import { useStoreConversation } from "./chat/useStoreConversation";

const SAVED_TIMEOUT_MS = 3000;

/**
 * useChatHook — composition root for the chat experience.
 *
 * Phase 5 of plans/useChatHook-enterprise-grade.md introduces an
 * explicit state machine for the action lifecycle. `isStreaming` and
 * `storeStatus` are now derived selectors over the reducer state
 * rather than independent useState booleans owned by sub-hooks.
 *
 * Network-level loading state from React Query (`isLoadingConversation`,
 * `isCreatingConversation`) deliberately stays out of the reducer —
 * those are different concerns and React Query is the source of truth.
 */
export function useChatHook() {
  const { setIsChatStarted, isChatStarted } = useGlobalContext();
  const clientId = useClientId();
  const { conversationId } = useParams();

  const [chatState, dispatch] = useReducer(chatReducer, initialChatState);

  const conversation = useActiveConversation({ conversationId, clientId });
  const newConv = useNewConversation({ clientId });
  const send = useSendMessage({ clientId, dispatch });
  const store = useStoreConversation({ clientId, dispatch });

  // Bridge React Query's conversation-fetch error into the reducer.
  // FETCH_ERROR is a no-op while the user has an active stream/save in
  // flight (see chatReducer); FETCH_RECOVERED clears stale fetch errors
  // when the query later succeeds.
  useEffect(() => {
    if (!conversation.error) {
      dispatch({ type: "FETCH_RECOVERED" });
      return;
    }
    const message =
      conversation.error?.response?.data?.error ||
      conversation.error?.message ||
      String(conversation.error);
    dispatch({ type: "FETCH_ERROR", message });
  }, [conversation.error]);

  // Auto-clear the "saved" toast after 3 s. Owned here rather than in
  // useStoreConversation so the timer's cleanup is attached to the
  // composition root's mount (useStoreConversation is now stateless).
  useEffect(() => {
    if (chatState.phase !== "saved") return undefined;
    const id = setTimeout(
      () => dispatch({ type: "SAVED_TIMEOUT" }),
      SAVED_TIMEOUT_MS,
    );
    return () => clearTimeout(id);
  }, [chatState.phase]);

  const createConversation = useCallback(
    async (text) => {
      const newConvId = await newConv.create(text);
      // Seed the active conversation under the new ID so the next
      // render sees a conversation object with `id` set, even before
      // the route changes.
      conversation.setActiveConversation({
        ...conversation.activeConversation,
        id: newConvId,
      });
      return newConvId;
    },
    [newConv, conversation],
  );

  const sendMessage = useCallback(
    async (targetConversationId, content) => {
      const contentToSend = (content || "").trim();
      if (!contentToSend || !targetConversationId) return;

      // Only flip the global flag when it's not already on, so we
      // don't schedule a no-op context update on every send.
      if (!isChatStarted) setIsChatStarted(true);

      const result = await send.sendMessage(
        targetConversationId,
        contentToSend,
      );

      if (result?.ok && result.conversation) {
        // Persist the post-stream snapshot. The reducer drives the
        // visible status; we swallow the rejection here so the promise
        // doesn't bubble as unhandled.
        store.save(result.conversation).catch(() => {});
      }
    },
    [isChatStarted, setIsChatStarted, send, store],
  );

  const regenerate = useCallback(async () => {
    const messages = conversation.activeConversation.messages;
    const lastUserMessage = [...messages]
      .reverse()
      .find((m) => m.role === "user")?.content;
    if (!lastUserMessage) return;

    const trimmed =
      messages.at(-1)?.role === "assistant"
        ? messages.slice(0, -1)
        : messages;
    if (trimmed !== messages) {
      conversation.setActiveConversation({
        ...conversation.activeConversation,
        messages: trimmed,
      });
    }
    await sendMessage(conversation.activeConversation.id, lastUserMessage);
  }, [conversation, sendMessage]);

  const dismissError = useCallback(
    () => dispatch({ type: "DISMISS_ERROR" }),
    [],
  );

  // Selectors derived from reducer state. The legacy `storeStatus`
  // shape is preserved so Chat.jsx's <StoreStatus /> banner reads the
  // same fields it always did.
  const isStreaming = chatState.phase === "streaming";
  const storeStatus = {
    loading: chatState.phase === "saving",
    success: chatState.phase === "saved",
    error: chatState.error?.kind === "store" ? chatState.error.message : null,
    data: chatState.saveData,
  };

  return {
    // conversation
    activeConversation: conversation.activeConversation,
    conversationId,

    // streaming + actions
    isStreaming,
    cancelStreaming: send.cancelStreaming,
    sendMessage,
    regenerate,

    // new-conversation flow
    createConversation,
    isCreatingConversation: newConv.isPending,

    // load state — `isFetching` is intentionally excluded so a
    // background refetch doesn't trigger the global <Loader />.
    isLoadingConversation: conversation.isLoading,

    // store status (legacy shape, derived from chatState).
    storeStatus,

    // unified error surface, plus the lifecycle phase for callers that
    // want it.
    error: chatState.error,
    dismissError,
    phase: chatState.phase,
  };
}
